// INTEGRATION DIAGNOSTICS — single source of truth for real-vs-fallback state.
// Every production-facing data path is classified into exactly one state so the
// UI can prove REAL data and never silently substitute demo content.

import { apiRequest } from "./client";
import { getToken } from "./auth";
import { ApiError } from "@/types/api";

export type IntegrationState = "REAL_DATA" | "FALLBACK" | "UNAVAILABLE" | "AUTH_ERROR" | "CHECKING";

export interface IntegrationDef {
  key: string;
  label: { tr: string; en: string };
  path: string;              // bridge path under /api/atlas
  method: "GET" | "POST";
  auth: boolean;             // requires Bearer token upstream
  uiRoute: string;           // route that consumes it
}

// The exact contract the web app depends on. Public read-only surface = primary
// SDIP source. Authenticated surface stays behind the Bearer token the owner must supply.
export const INTEGRATIONS: IntegrationDef[] = [
  { key: "overview", label: { tr: "Genel durum", en: "Overview" }, path: "/overview", method: "GET", auth: false, uiRoute: "/app" },
  { key: "live", label: { tr: "Canlı Maç Merkezi + Canlı Sinyaller", en: "LIVE Match Center + LIVE signals" }, path: "/live?limit=24", method: "GET", auth: false, uiRoute: "/app/live" },
  { key: "active-signals", label: { tr: "Aktif ATLAS sinyalleri", en: "Active ATLAS signals" }, path: "/active-signals?limit=12", method: "GET", auth: false, uiRoute: "/app/live" },
  { key: "prematch", label: { tr: "Maç Öncesi Kâşif + ATLAS Seçimleri", en: "Prematch Explorer + ATLAS Selections" }, path: "/prematch?grouped=true&limit=24", method: "GET", auth: false, uiRoute: "/app/prematch" },
  { key: "today-performance", label: { tr: "Sonuçlanan sonuçlar / performans", en: "Resolved results / performance" }, path: "/today-performance", method: "GET", auth: false, uiRoute: "/app/performance" },
  { key: "feed", label: { tr: "Bildirim akışı", en: "Notification feed" }, path: "/feed?limit=8", method: "GET", auth: false, uiRoute: "/app/notifications" },
  { key: "match-detail", label: { tr: "Maç detay / dossier", en: "Match detail / dossier" }, path: "/live?limit=24", method: "GET", auth: false, uiRoute: "/app/match/:id" },
  { key: "atlas-prematch-v2", label: { tr: "ATLAS Prematch v2 (zenginleştirilmiş)", en: "ATLAS Prematch v2 (enriched)" }, path: "/prematch-v2", method: "GET", auth: true, uiRoute: "/app/prematch" },
  { key: "atlas-assistant", label: { tr: "ATLAS Asistan", en: "ATLAS Assistant" }, path: "/assistant", method: "POST", auth: true, uiRoute: "/app/atlas" },
  { key: "auth", label: { tr: "SDIP kimlik doğrulama (/auth/me)", en: "SDIP authentication (/auth/me)" }, path: "/auth/me", method: "GET", auth: true, uiRoute: "/login" },
  { key: "membership", label: { tr: "Üyelik / tier (/auth/me profili)", en: "Membership / tier (/auth/me profile)" }, path: "/auth/me", method: "GET", auth: true, uiRoute: "/app/membership" },
  { key: "enrichment", label: { tr: "FootyStats zenginleştirme", en: "FootyStats enrichment" }, path: "/enrich/status", method: "GET", auth: false, uiRoute: "/app/match/:id" },
];

export interface IntegrationResult {
  def: IntegrationDef;
  state: IntegrationState;
  status: number | null;      // HTTP status observed
  detail: string;             // human/diagnostic detail
  missing?: string;           // exact missing dependency when not REAL_DATA
}

function isNonEmpty(data: any): boolean {
  if (data == null) return false;
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows.length > 0;
    return Object.keys(data).length > 0;
  }
  return true;
}

export async function probeIntegration(def: IntegrationDef): Promise<IntegrationResult> {
  const hasToken = !!getToken();
  try {
    const data = await apiRequest<any>(def.path, {
      method: def.method,
      body: def.method === "POST" ? { message: "diagnostic ping" } : undefined,
      auth: def.auth,
      retries: 0,
    });
    // enrichment status is a special case: configured flag decides real vs unavailable
    if (def.key === "enrichment") {
      const configured = !!data?.configured;
      return configured
        ? { def, state: "REAL_DATA", status: 200, detail: `source=${data?.source || "?"}` }
        : { def, state: "UNAVAILABLE", status: 200, detail: "FOOTYSTATS_API_KEY not set", missing: "FOOTYSTATS_API_KEY" };
    }
    const real = isNonEmpty(data);
    return {
      def,
      state: real ? "REAL_DATA" : "UNAVAILABLE",
      status: 200,
      detail: real ? "live production data" : "empty payload",
      missing: real ? undefined : "non-empty upstream response",
    };
  } catch (e) {
    const err = e as ApiError;
    const status = err.status ?? null;
    if (err.kind === "unauthorized" || err.kind === "forbidden") {
      return {
        def,
        state: "AUTH_ERROR",
        status,
        detail: "Bearer token required upstream",
        missing: hasToken ? "valid/authorized SDIP Bearer token" : "SDIP Bearer token (SDIP_AUTH)",
      };
    }
    if (err.kind === "notfound") {
      return { def, state: "UNAVAILABLE", status, detail: "endpoint not found upstream", missing: "confirmed SDIP endpoint contract" };
    }
    return { def, state: "UNAVAILABLE", status, detail: err.message || "upstream unavailable", missing: "reachable SDIP backend" };
  }
}

// Classify a live react-query result (used for inline per-section state chips).
export function classifyQuery(q: { isLoading: boolean; isError: boolean; error?: ApiError | null; data?: any }): IntegrationState {
  if (q.isLoading) return "CHECKING";
  if (q.isError) {
    const k = q.error?.kind;
    if (k === "unauthorized" || k === "forbidden") return "AUTH_ERROR";
    return "UNAVAILABLE";
  }
  return isNonEmpty(q.data) ? "REAL_DATA" : "UNAVAILABLE";
}

export const STATE_META: Record<IntegrationState, { tr: string; en: string; tone: string }> = {
  REAL_DATA: { tr: "GERÇEK VERİ", en: "REAL DATA", tone: "border-atlas/30 bg-atlas/10 text-atlas-300" },
  FALLBACK: { tr: "YEDEK", en: "FALLBACK", tone: "border-gold/30 bg-gold/10 text-gold-300" },
  UNAVAILABLE: { tr: "MEVCUT DEĞİL", en: "UNAVAILABLE", tone: "border-white/15 bg-white/5 text-muted-foreground" },
  AUTH_ERROR: { tr: "YETKİ GEREKLİ", en: "AUTH REQUIRED", tone: "border-live/30 bg-live/10 text-live-400" },
  CHECKING: { tr: "KONTROL EDİLİYOR", en: "CHECKING", tone: "border-white/15 bg-white/5 text-muted-foreground" },
};
